import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
  DialogClose
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input, InputProps } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox"; // Import Checkbox
import { Send, MessageSquare, EyeOff, Star } from 'lucide-react';
import { showError } from '@/utils/toast';
import { ForumTopic } from '@/pages/ForumTopicList';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import TipTapEditor from './TipTapEditor'; // Importáljuk a TipTap szerkesztőt

type TopicSaveData = Pick<ForumTopic, 'title' | 'description' | 'isHidden' | 'isHighlighted' | 'isReadOnly'> & { subscribeToTopic?: boolean };

interface EditTopicModalProps {
  isOpen: boolean;
  //azért legyen ott az is hiba esetén tudjunk mit kezdeni
  onOpenChange: (open: boolean) => void;
  categoryId: string | null; // Hozzáadjuk a kategória ID-t
  topic: ForumTopic | null;
  onSave: (data: TopicSaveData) => void;
}

const EditTopicModal: React.FC<EditTopicModalProps> = ({ isOpen, onOpenChange, categoryId, topic, onSave }) => {
  const [categories, setCategories] = useState<any[]>([]);
  const { isAdmin } = useAuth();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isHidden, setIsHidden] = useState(false);
  const [isHighlighted, setIsHighlighted] = useState(false);
  const [isReadOnly, setIsReadOnly] = useState(false); // Új állapot a read_only-hoz
  const [subscribeToTopic, setSubscribeToTopic] = useState(true); // Új állapot a feliratkozáshoz
  const [titleValidation, setTitleValidation] = useState<{ message: string; isError: boolean } | null>(null);
  const [isCheckingTitle, setIsCheckingTitle] = useState(false);
  const [initialState, setInitialState] = useState<TopicSaveData | null>(null);
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false);
  const isEditing = !!topic;

  useEffect(() => {
    // Csak akkor frissítjük az állapotot, ha a modal megnyílik
    if (isOpen) {
      let currentData: TopicSaveData;
      if (topic) {
        // Szerkesztés mód: betöltjük a meglévő adatokat
        currentData = {
          title: topic.title,
          description: topic.description,
          isHidden: topic.isHidden || false,
          isHighlighted: topic.isHighlighted || false,
        };
      } else {
        // Létrehozás mód: kiürítjük a mezőket
        currentData = {
          title: '',
          description: '',
          isHidden: false,
          isHighlighted: false,
          isReadOnly: false, // Új téma esetén alapértelmezett
        };
      }
      setTitle(currentData.title);
      setDescription(currentData.description);
      setIsHidden(currentData.isHidden);
      setIsHighlighted(currentData.isHighlighted);
      // isReadOnly betöltése szerkesztéskor
      setIsReadOnly(topic?.isReadOnly || false);
      setSubscribeToTopic(true); // Alapértelmezetten bekapcsolva új téma esetén
      setInitialState(currentData); // Eltároljuk a kezdeti állapotot az összehasonlításhoz
      // Modal megnyitásakor töröljük a hibát
      setTitleValidation(null);
    }
  }, [topic, isOpen]);

  // Debounced title check
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handler = setTimeout(async () => {
      const trimmedTitle = title.trim();
      // Ne ellenőrizzük, ha a cím nem változott szerkesztéskor
      if (isEditing && topic && trimmedTitle === topic.title) {
        setTitleValidation(null);
        return;
      }
      if (trimmedTitle.length < 3) {
        setTitleValidation(null);
        return;
      }
      setIsCheckingTitle(true);
      try {
        const params = new URLSearchParams({ title: trimmedTitle });
        if (isEditing && topic) {
          params.append('topic_id', topic.id);
          params.append('forum_category_id', topic.categoryId);
        } else if (categoryId) { // Új téma létrehozásakor
          params.append('forum_category_id', categoryId);
        }
        const apiUrl = `/api/forum/topics/check_title?${params.toString()}`;
        const response = await fetch(apiUrl);
        const data = await response.json();
        if (data.exists) {
          setTitleValidation({ message: 'Ez a téma cím már foglalt.', isError: true });
        } else {
          setTitleValidation({ message: 'A cím szabad.', isError: false });
        }
      } catch (error) {
        console.error("Cím ellenőrzési hiba:", error);
        // Hiba esetén inkább engedjük tovább, a backend majd validálja.
        setTitleValidation(null);
      } finally {
        setIsCheckingTitle(false);
      }
    }, 500); // 500ms késleltetés gépelés után

    return () => {
      clearTimeout(handler);
    }; // A categoryId-t is hozzáadjuk a függőségekhez
  }, [title, isOpen, isEditing, topic, categoryId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !description.trim() || titleValidation?.isError) {
      showError('A cím és a leírás nem lehet üres, és a címnek egyedinek kell lennie!');
      return;
    }

    onSave({
      title: title.trim(),
      description: description.trim(),
      isHidden,
      isHighlighted,
      isReadOnly,
      subscribeToTopic: !isEditing ? subscribeToTopic : undefined, // Csak új téma esetén küldjük el
    });
    onOpenChange(false);
  };

  const hasChanges = () => {
    if (!initialState) return false;
    const currentDescription = description.trim() === '<p></p>' ? '' : description.trim();
    const initialDescription = initialState.description.trim() === '<p></p>' ? '' : initialState.description.trim();

    return (
      title.trim() !== initialState.title.trim() ||
      currentDescription !== initialDescription ||
      isHidden !== initialState.isHidden ||
      isHighlighted !== initialState.isHighlighted ||
      isReadOnly !== initialState.isReadOnly
    );
  };

  const handleCloseAttempt = () => {
    if (hasChanges()) {
      setIsConfirmCloseOpen(true);
    } else {
      onOpenChange(false);
    }
  };

  const handleModalOpenChange = (open: boolean) => {
    if (!open) {
      handleCloseAttempt();
    } else {
      onOpenChange(true);
    }
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleModalOpenChange}>
        <DialogContent className="sm:max-w-[1250px] bg-card text-card-foreground border-border shadow-lg flex flex-col max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-primary flex items-center gap-2">
              <MessageSquare size={24} /> {topic ? 'Téma szerkesztése' : 'Új téma létrehozása'}
            </DialogTitle>
            <DialogDescription>Add meg az új téma címét és a nyitó hozzászólás tartalmát.</DialogDescription>
          </DialogHeader>
          <div className="flex-grow overflow-y-auto pr-4 -mr-4"> {/* Görgethető konténer */}
            <form
              id="topic-form"
              onSubmit={handleSubmit}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'BUTTON') {
                  e.preventDefault();
                }
              }}
              className="grid gap-4 py-4"
            >
              <div>
                <Label htmlFor="edit-topic-title" className="text-muted-foreground">Téma címe</Label>
                <Input
                  id="edit-topic-title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className={cn("mt-1 bg-secondary", titleValidation?.isError && "border-destructive focus-visible:ring-destructive")}
                  required
                />
                {isCheckingTitle && <p className="text-xs text-muted-foreground mt-1">Cím ellenőrzése...</p>}
                {titleValidation && (
                  <p className={cn("text-xs mt-1", titleValidation.isError ? "text-destructive" : "text-green-500")}>
                    {titleValidation.message}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="edit-topic-desc" className="text-muted-foreground">Leírás (első hozzászólás)</Label>
                <p className="text-xs text-muted-foreground mb-2">Képek és fájlok (pl. ZIP, RAR) beilleszthetők a szerkesztővel.</p>
                <TipTapEditor
                  value={description}
                  onChange={setDescription}
                  placeholder="Írd ide a téma leírását, ami egyben az első hozzászólás is lesz..."
                />
              </div>
              {!isEditing && (
                <div className="flex items-center space-x-2 p-3 bg-secondary rounded-md opacity-60">
                  <Checkbox
                    id="subscribe-to-topic"
                    checked={subscribeToTopic}
                    onCheckedChange={(checked) => setSubscribeToTopic(checked as boolean)}
                    disabled={true}
                  />
                  <Label htmlFor="subscribe-to-topic" className="cursor-not-allowed">
                    Feliratkozás a témára <span className="text-yellow-400 font-semibold text-sm">(átmenetileg nem működik)</span>
                  </Label>
                </div>
              )}
              {isAdmin && (
                <div className="grid grid-cols-2 gap-4 mt-2">
                  <div className="flex items-center space-x-2 p-3 bg-secondary rounded-md">
                    <Switch id="is-hidden" checked={isHidden} onCheckedChange={setIsHidden} />
                    <Label htmlFor="is-hidden" className="flex items-center gap-2 cursor-pointer">
                      <EyeOff size={16} /> Téma elrejtése
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 bg-secondary rounded-md">
                    <Switch id="is-highlighted" checked={isHighlighted} onCheckedChange={setIsHighlighted} />
                    <Label htmlFor="is-highlighted" className="flex items-center gap-2 cursor-pointer">
                      <Star size={16} /> Téma kiemelése
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2 p-3 bg-secondary rounded-md">
                    <Switch id="is-read-only" checked={isReadOnly} onCheckedChange={setIsReadOnly} />
                    <Label htmlFor="is-read-only" className="flex items-center gap-2 cursor-pointer">
                      <EyeOff size={16} /> Téma csak olvasható
                    </Label>
                  </div>
                </div>
              )}
            </form>
          </div>
          <DialogFooter className="flex-shrink-0 pt-4 border-t border-border">
            <Button type="button" variant="outline" onClick={handleCloseAttempt}>Mégsem</Button>
            <Button type="submit" form="topic-form" disabled={!!titleValidation?.isError || isCheckingTitle}><Send size={16} className="mr-2" /> Mentés</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={isConfirmCloseOpen} onOpenChange={setIsConfirmCloseOpen}>
        <AlertDialogContent className="bg-card text-card-foreground border-border">
          <AlertDialogHeader>
            <AlertDialogTitle>Biztosan bezárod?</AlertDialogTitle>
            <AlertDialogDescription>
              Nem mentett módosításaid vannak. Ha bezárod az ablakot, a változtatások elvesznek.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Mégsem</AlertDialogCancel>
            <AlertDialogAction onClick={() => onOpenChange(false)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Bezárás mentés nélkül
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default EditTopicModal;